import type { AxiosError } from 'axios';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Mail,
  ArrowRight,
  ArrowLeft,
  Shield,
  Clock,
  CheckCircle2,
  KeyRound,
  MailCheck,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import authService from '@/services/auth.service';
import { BRAND_HERO_GRADIENT, BRAND_TEAL } from '@/lib/brand';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Campo obrigatório",
        description: "Por favor, informe seu e-mail",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      await authService.requestPasswordReset(email.trim());
      setSubmitted(true);

      toast({
        title: "E-mail enviado",
        description: "Se o endereço estiver cadastrado, você receberá as instruções.",
      });
    } catch (error: unknown) {
      toast({
        title: "Falha ao enviar e-mail",
        description: (error as AxiosError<{ detail?: string }>)?.response?.data?.detail ?? "Tente novamente em instantes.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: Shield,
      title: 'Recuperação Segura',
      description: 'Processo protegido para garantir a segurança da sua conta.',
    },
    {
      icon: Clock,
      title: 'Link Temporário',
      description: 'O link de recuperação expira em 60 minutos por segurança.',
    },
    {
      icon: KeyRound,
      title: 'Nova Senha',
      description: 'Crie uma senha forte para proteger suas informações.',
    },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Section - Hero */}
      <div
        className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden"
        style={{ background: BRAND_HERO_GRADIENT }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        {/* Logo */}
        <div className="relative z-10">
          <Link to="/">
            <img
              src="/logoSidebar.png"
              alt="Librattum"
              className="h-10"
            />
          </Link>
        </div>

        {/* Content */}
        <div className="relative z-10 space-y-8">
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-white leading-tight">
              Esqueceu sua senha?<br />
              <span className="text-[#F5B322]">Não se preocupe</span>
            </h1>
            <p className="text-white/80 text-lg max-w-md">
              Acontece com todo mundo. Vamos ajudá-lo a recuperar o acesso à sua conta de forma rápida e segura.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 rounded-xl bg-white/10 border border-white/20 backdrop-blur-sm"
              >
                <div className="p-2 rounded-lg bg-[#F5B322]/20">
                  <feature.icon className="h-5 w-5 text-[#F5B322]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{feature.title}</h3>
                  <p className="text-sm text-white/70">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 text-white/60 text-sm">
          © {new Date().getFullYear()} Librattum. Todos os direitos reservados.
        </div>
      </div>

      {/* Right Section - Form */}
      <div className="flex-1 flex items-center justify-center p-8 brand-light-surface">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/">
              <img
                src="/logo-librattum.png"
                alt="Librattum"
                className="h-10 mx-auto"
              />
            </Link>
          </div>

          {submitted ? (
            /* Success State */
            <div className="text-center space-y-6">
              <div className="mx-auto w-20 h-20 rounded-full bg-[#F5B322]/20 flex items-center justify-center">
                <MailCheck className="h-10 w-10 text-[#A1700F]" />
              </div>
              
              <div className="space-y-2">
                <h2 className="text-3xl font-bold text-gray-900">
                  Verifique seu e-mail
                </h2>
                <p className="text-gray-600 max-w-sm mx-auto">
                  Enviamos instruções de recuperação para <strong className="text-gray-900">{email}</strong>
                </p>
              </div>

              <div className="bg-[#FFF7E7] border border-[#F5D38C] rounded-xl p-4 text-sm text-[#7B5715]">
                <p>
                  Não encontrou o e-mail? Verifique sua pasta de spam ou lixo eletrônico.
                </p>
              </div>

              <div className="space-y-3 pt-4">
                <Button
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  className="w-full h-12"
                >
                  Tentar outro e-mail
                </Button>
                
                <Link to="/login">
                  <Button
                    variant="ghost"
                    className="w-full h-12 text-gray-600"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Voltar ao login
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            /* Form State */
            <>
              {/* Header */}
              <div className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-[#F5B322]/20 flex items-center justify-center mb-6">
                  <KeyRound className="h-8 w-8 text-[#A1700F]" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Recuperar senha
                </h2>
                <p className="mt-2 text-gray-600">
                  Digite seu e-mail para receber as instruções
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700">
                    E-mail cadastrado
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      className="pl-11 h-12 bg-white border-gray-200 focus-visible:border-[#F5B322] focus-visible:ring-[#F5B322]"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold bg-[#F5B322] hover:bg-[#D89A15] text-[#2F2117]"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 border-2 border-[#2F2117]/30 border-t-[#2F2117] rounded-full animate-spin" />
                      Enviando...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      Enviar instruções
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  )}
                </Button>
              </form>

              {/* Back to Login */}
              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 transition-colors hover:opacity-80"
                  style={{ color: BRAND_TEAL }}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Voltar ao login
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="flex items-center justify-center gap-6 pt-4 text-sm text-gray-500">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-[#F5B322]" />
                  Processo seguro
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-[#F5B322]" />
                  Link temporário
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
